/**
 * How much of the viewport an image container spans. Galleries are placed in very
 * different columns (full bleed, one half of a two-column grid, a narrow sidebar),
 * so the layout that renders the images cannot know their real display width on
 * its own — the caller passes the hint down and every image derives its `sizes`
 * from it.
 *
 * Without this, a single hardcoded `sizes` makes next/image request the widest
 * candidate everywhere and Lighthouse reports the difference as wasted bytes.
 */
export type ImageWidthHint = {
    /** Share of the viewport the container spans below `lg` (1024px), in vw. */
    mobileVw: number;
    /** Share of the viewport the container spans from `lg` up, in vw. */
    desktopVw: number;
};

/** Container spans the full viewport at every breakpoint. */
export const FULL_WIDTH_HINT: ImageWidthHint = { mobileVw: 100, desktopVw: 100 };

/** Container spans the full viewport on mobile and one half of it from `lg` up. */
export const HALF_WIDTH_HINT: ImageWidthHint = { mobileVw: 100, desktopVw: 50 };

const LG_BREAKPOINT = '1024px';

/**
 * `sizes` for an image that fills `fraction` of a container described by `hint`
 * (`0.5` → half the container width). Rounded up to the next whole vw so the
 * chosen candidate is never smaller than the display size.
 */
export function imageSizes(hint: ImageWidthHint = FULL_WIDTH_HINT, fraction = 1): string {
    const mobile = toVw(hint.mobileVw, fraction);
    const desktop = toVw(hint.desktopVw, fraction);

    if (mobile === desktop) return `${mobile}vw`;

    return `(max-width: ${LG_BREAKPOINT}) ${mobile}vw, ${desktop}vw`;
}

function toVw(containerVw: number, fraction: number): number {
    return Math.min(100, Math.max(1, Math.ceil(containerVw * fraction)));
}
